/* eslint-disable react/jsx-props-no-spreading */
import type { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
// eslint-disable-next-line no-use-before-define
import React from 'react';
import { useRouter } from 'next/router';

import { logger } from '@shared/utils';
import { InViewProvider } from '@shared/v2/components/InView';

import { Meta } from '@/v2/components/global/Meta';
import { LoadingView } from '@/v2/components/pages/LoadingView';
import { PackageView } from '@/v2/components/pages/PackageViewV2';
import { FlightModalProvider } from '@/v2/components/pages/PackageViewV2/contexts/FlightModalContext';
import { OtherPackageModalProvider } from '@/v2/components/pages/PackageViewV2/contexts/OtherPackageModalContext';
import { SearchPanelControlsProvider } from '@/v2/components/pages/PackageViewV2/contexts/SearchPanelContext';
import { CampaignProvider } from '@/v2/contexts/campaign';
import { getPackagePage } from '@/v2/services/bff/getPackagePage';
import { GetPackageOffersProvider } from '../../../../../../components/PackagePage/hooks/useGetPackageOffers';
import { ProcuredOfferProvider } from '../../../../../../components/PackagePage/hooks/useProcuredOffer';
// using legacy context
import { DestinationProvider } from '../../../../../../hooks/useDestination';
import { PropertyContentProvider } from '../../../../../../hooks/usePropertyContent';

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps = async ({
  params,
  preview,
}: GetStaticPropsContext<{ destinationName: string; propertyId: string }>) => {
  try {
    const props = await getPackagePage({
      destinationName: params?.destinationName,
      propertyId: params?.propertyId,
      preview,
    });

    return {
      props,
      revalidate: 300,
    };
  } catch (error) {
    logger.error(error);
    return {
      props: {
        campaign: null,
        propertyContent: null,
        procuredOffer: null,
        meta: null,
        importantInformation: null,
        navigation: null,
        informationMessage: null,
        warningMessage: null,
        marketingMessage: null,
      },
      notFound: true,
      revalidate: 300,
    };
  }
};

const PackagePage = ({
  campaign,
  propertyContent,
  procuredOffer,
  meta,
  importantInformation,
  navigation,
  informationMessage,
  warningMessage,
  marketingMessage,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const router = useRouter();

  // If the page is not yet generated, this will be displayed
  // initially until getStaticProps() finishes running
  if (router.isFallback) {
    return <LoadingView />;
  }

  return (
    propertyContent && (
      <InViewProvider>
        <FlightModalProvider>
          <OtherPackageModalProvider>
            <DestinationProvider value={propertyContent.destination}>
              <CampaignProvider value={campaign}>
                <PropertyContentProvider value={propertyContent}>
                  <ProcuredOfferProvider value={procuredOffer}>
                    <GetPackageOffersProvider>
                      <SearchPanelControlsProvider>
                        <Meta {...meta} />
                        <PackageView
                          campaign={campaign}
                          propertyContent={propertyContent}
                          procuredOffer={procuredOffer}
                          importantInformation={importantInformation}
                          navigation={navigation}
                          informationMessage={informationMessage}
                          warningMessage={warningMessage}
                          marketingMessage={marketingMessage}
                        />
                      </SearchPanelControlsProvider>
                    </GetPackageOffersProvider>
                  </ProcuredOfferProvider>
                </PropertyContentProvider>
              </CampaignProvider>
            </DestinationProvider>
          </OtherPackageModalProvider>
        </FlightModalProvider>
      </InViewProvider>
    )
  );
};
export default PackagePage;
